import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useForm } from 'react-hook-form';
import axios from 'axios';
import TermsModal from '../components/TermsModal';
import PriceDisplay from '../components/PriceDisplay';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

const CustomerPage = () => {
  const { register, handleSubmit, watch, reset, formState: { errors } } = useForm({
    defaultValues: {
      fullName: '',
      email: '',
      phone: '',
      cryptoType: 'BTC',
      amount: '',
    }
  });

  const [loading, setLoading] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [rates, setRates] = useState(null);
  const [successData, setSuccessData] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [termsAccepted, setTermsAccepted] = useState(false);

  const cryptoType = watch('cryptoType');
  const amount = watch('amount');

  // Fetch rates on component mount
  useEffect(() => {
    fetchRates();
    const interval = setInterval(fetchRates, 30000); // Update every 30 seconds
    return () => clearInterval(interval);
  }, []);

  const fetchRates = async () => {
    try {
      const response = await axios.get(`${API_BASE}/rates`);
      if (response.data.success) {
        setRates(response.data.rates);
      }
    } catch (error) {
      console.error('Error fetching rates:', error);
    }
  };

  const onSubmit = async (data) => {
    if (!termsAccepted) {
      setErrorMessage('You must accept the terms and agreement');
      return;
    }

    setLoading(true);
    setErrorMessage(null);
    setSuccessData(null);

    try {
      const response = await axios.post(`${API_BASE}/orders/create`, data);
      
      if (response.data.success) {
        setSuccessData({
          orderId: response.data.orderId,
          appliedRate: response.data.appliedRate
        });
        reset();
        setTermsAccepted(false);
        
        // Clear message after 5 seconds
        setTimeout(() => setSuccessData(null), 5000);
      }
    } catch (error) {
      setErrorMessage(error.response?.data?.error || 'Failed to create order');
    } finally {
      setLoading(false);
    }
  };

  const currentRate = rates ? rates[cryptoType] : null;
  const estimatedPrice = currentRate && amount ? (parseFloat(amount) * currentRate).toFixed(2) : 0;
  const commission = (estimatedPrice * 0.1).toFixed(2);
  const total = (parseFloat(estimatedPrice) + parseFloat(commission)).toFixed(2);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
      {/* Animated background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <motion.div
          className="absolute top-0 left-0 w-96 h-96 bg-blue-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20"
          animate={{ x: [0, 100, 0], y: [0, 50, 0] }}
          transition={{ duration: 8, repeat: Infinity }}
        />
      </div>

      <div className="relative z-10 max-w-2xl mx-auto px-8 py-16">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl font-bold text-white mb-2">Buy Digital currency</h1>
          <p className="text-gray-300">Quick, secure, and transparent</p>
        </motion.div>

        {/* Success/Error Messages */}
        <AnimatePresence>
          {successData && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="mb-6 p-4 bg-green-500/20 border border-green-500 text-green-300 rounded-lg"
            >
              <p className="font-semibold">✓ Order created successfully</p>
              <p className="text-sm mt-1">Order ID: {successData.orderId}</p>
              {successData.appliedRate && (
                <div className="text-sm mt-2 pt-2 border-t border-green-400/30 space-y-1">
                  <p>
                    Applied Rate ({successData.appliedRate.cryptoType}):
                    {' '}
                    ${Number(successData.appliedRate.usdPerCoin).toLocaleString()}
                  </p>
                  <p>Rate Source: {successData.appliedRate.source}</p>
                  <p>Rate Time: {new Date(successData.appliedRate.fetchedAt).toLocaleString()}</p>
                </div>
              )}
            </motion.div>
          )}
          {errorMessage && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="mb-6 p-4 bg-red-500/20 border border-red-500 text-red-300 rounded-lg"
            >
              ✕ {errorMessage}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Main Form Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="card p-8 backdrop-blur-xl bg-white/10 border border-white/20"
        >
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* Crypto Type */}
            <div>
              <label className="block text-white font-semibold mb-2">Digital currency</label>
              <select
                {...register('cryptoType')}
                className="input-field bg-white/10 text-white border-white/30"
              >
                <option value="BTC" className="bg-gray-900">Bitcoin (BTC)</option>
                <option value="ETH" className="bg-gray-900">Ethereum (ETH)</option>
                <option value="USDT" className="bg-gray-900">Tether (USDT)</option>
              </select>
            </div>

            {/* Amount */}
            <div>
              <label className="block text-white font-semibold mb-2">Amount ({cryptoType})</label>
              <input
                type="number"
                step="0.001"
                placeholder="0.00"
                {...register('amount', {
                  required: 'Amount is required',
                  min: { value: 0, message: 'Amount must be positive' }
                })}
                className="input-field bg-white/10 text-white border-white/30 placeholder-gray-400"
              />
              {errors.amount && <p className="text-red-400 text-sm mt-1">{errors.amount.message}</p>}
            </div>

            {/* Price Display */}
            {amount && currentRate && (
              <PriceDisplay
                cryptoType={cryptoType}
                amount={amount}
                rate={currentRate}
                estimatedPrice={estimatedPrice}
                commission={commission}
                total={total}
              />
            )}

            {/* Full Name */}
            <div>
              <label className="block text-white font-semibold mb-2">Full Name</label>
              <input
                type="text"
                placeholder="John Doe"
                {...register('fullName', { required: 'Full name is required' })}
                className="input-field bg-white/10 text-white border-white/30 placeholder-gray-400"
              />
              {errors.fullName && <p className="text-red-400 text-sm mt-1">{errors.fullName.message}</p>}
            </div>

            {/* Email */}
            <div>
              <label className="block text-white font-semibold mb-2">Email</label>
              <input
                type="email"
                placeholder="your@email.com"
                {...register('email', {
                  required: 'Email is required',
                  pattern: { value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i, message: 'Invalid email' }
                })}
                className="input-field bg-white/10 text-white border-white/30 placeholder-gray-400"
              />
              {errors.email && <p className="text-red-400 text-sm mt-1">{errors.email.message}</p>}
            </div>

            {/* Phone */}
            <div>
              <label className="block text-white font-semibold mb-2">Phone Number</label>
              <input
                type="tel"
                placeholder="+94 123 456 7890"
                {...register('phone', { required: 'Phone number is required' })}
                className="input-field bg-white/10 text-white border-white/30 placeholder-gray-400"
              />
              {errors.phone && <p className="text-red-400 text-sm mt-1">{errors.phone.message}</p>}
            </div>

            {/* Terms Checkbox */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex items-start gap-3"
            >
              <input
                type="checkbox"
                id="terms"
                checked={termsAccepted}
                onChange={(e) => setTermsAccepted(e.target.checked)}
                className="mt-1 w-5 h-5 rounded border-white/30 text-blue-600 cursor-pointer"
              />
              <label htmlFor="terms" className="text-gray-300 cursor-pointer">
                I agree to the{' '}
                <button
                  type="button"
                  onClick={() => setShowTerms(true)}
                  className="text-blue-400 hover:text-blue-300 underline"
                >
                  Terms and Agreement
                </button>
              </label>
            </motion.div>

            {/* Submit Button */}
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              disabled={loading || !termsAccepted}
              type="submit"
              className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed py-4 text-lg font-bold"
            >
              {loading ? 'Processing...' : 'Place Order'}
            </motion.button>
          </form>
        </motion.div>

        {/* Terms Modal */}
        <TermsModal isOpen={showTerms} onClose={() => setShowTerms(false)} />

        {/* Footer */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="text-center text-gray-400 text-sm mt-12"
        >
          Your data is secure. We will contact you shortly after receiving your order.
        </motion.p>
      </div>
    </div>
  );
};

export default CustomerPage;
